import { Trans, useLingui } from '@lingui/react/macro'

import { Button, Card, Group, Stack, Text, TextInput } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconUser } from '@tabler/icons-react'
import { useState } from 'react'
import { userHooks } from '$src/lib/trpcHooks'

interface PersonalInfoCardProps {
  user: {
    id: number
    firstName: string
    lastName: string
    email: string | null
  }
}

export function PersonalInfoCard({ user }: PersonalInfoCardProps) {
  const { t } = useLingui()

  const handleEdit = () => {
    modals.open({
      title: t`Edit information`,
      centered: true,
      children: <EditPersonalInfoForm user={user} />,
    })
  }

  return (
    <Card radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="sm">
            <IconUser size={20} />
            <Text fw={600}>
              <Trans>Personal information</Trans>
            </Text>
          </Group>
          <Button variant="light" size="xs" onClick={handleEdit}>
            <Trans>Edit</Trans>
          </Button>
        </Group>

        <Stack gap="xs">
          <div>
            <Text size="xs" c="dimmed">
              <Trans>Name</Trans>
            </Text>
            <Text>
              {user.firstName} {user.lastName}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              <Trans>Email</Trans>
            </Text>
            <Text>{user.email}</Text>
          </div>
        </Stack>
      </Stack>
    </Card>
  )
}

function EditPersonalInfoForm({
  user,
}: {
  user: PersonalInfoCardProps['user']
}) {
  const { t } = useLingui()
  const [firstName, setFirstName] = useState(user.firstName)
  const [lastName, setLastName] = useState(user.lastName)
  const { mutateAsync: updateName, isPending } = userHooks.useUpdate()

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      return
    }

    await updateName({
      id: user.id,
      data: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      },
    })
    modals.closeAll()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isPending) {
      handleSubmit()
    }
  }

  return (
    <Stack gap="md">
      <TextInput
        label={t`First name`}
        value={firstName}
        onChange={(e) => setFirstName(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        required
      />
      <TextInput
        label={t`Last name`}
        value={lastName}
        onChange={(e) => setLastName(e.currentTarget.value)}
        onKeyDown={handleKeyDown}
        required
      />
      <Button onClick={handleSubmit} loading={isPending}>
        <Trans>Save</Trans>
      </Button>
    </Stack>
  )
}
